import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { ArrowBack, Edit, Delete, Save, Cancel } from '@mui/icons-material';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MainLayout } from '../components/layout/MainLayout';
import { db } from '../lib/firebase';
import { Notice } from '../types/notice';

export const NoticeDetailPage: React.FC = () => {
  const { noticeId } = useParams<{ noticeId: string }>();
  const navigate = useNavigate();

  const [notice, setNotice] = useState<Notice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 수정 모드 상태
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editIsUrgent, setEditIsUrgent] = useState(false);
  const [updating, setUpdating] = useState(false);

  // 삭제 확인 다이얼로그
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 공지사항 정보 불러오기
  useEffect(() => {
    const fetchNotice = async () => {
      if (!noticeId) return;

      try {
        const docRef = doc(db, 'notices', noticeId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() } as Notice;
          setNotice(data);
          setEditTitle(data.title);
          setEditContent(data.content);
          setEditIsUrgent(data.isUrgent);
        } else {
          setError('공지사항을 찾을 수 없습니다.');
        }
      } catch (err) {
        console.error('Error fetching notice:', err);
        setError('공지사항 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchNotice();
  }, [noticeId]);

  const handleBackToList = () => {
    navigate('/notices');
  };

  const handleEditMode = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (notice) {
      setEditTitle(notice.title);
      setEditContent(notice.content);
      setEditIsUrgent(notice.isUrgent);
    }
    setIsEditMode(false);
    setError('');
  };

  const handleUpdate = async () => {
    if (!noticeId || !notice) return;

    if (!editTitle.trim()) {
      setError('제목을 입력해주세요.');
      return;
    }

    if (!editContent.trim()) {
      setError('내용을 입력해주세요.');
      return;
    }

    setUpdating(true);
    setError('');

    try {
      await updateDoc(doc(db, 'notices', noticeId), {
        title: editTitle.trim(),
        content: editContent.trim(),
        isUrgent: editIsUrgent,
      });

      // 상태 업데이트
      setNotice({
        ...notice,
        title: editTitle.trim(),
        content: editContent.trim(),
        isUrgent: editIsUrgent,
      });

      setIsEditMode(false);
    } catch (err) {
      console.error('Error updating notice:', err);
      setError('공지사항 수정에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!noticeId) return;

    setDeleting(true);

    try {
      await deleteDoc(doc(db, 'notices', noticeId));
      navigate('/notices');
    } catch (err) {
      console.error('Error deleting notice:', err);
      setError('공지사항 삭제에 실패했습니다. 다시 시도해주세요.');
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (!notice) {
    return (
      <MainLayout>
        <Box>
          <Alert severity="error">{error || '공지사항을 찾을 수 없습니다.'}</Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToList}
            sx={{ mt: 2 }}
          >
            목록으로 돌아가기
          </Button>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        {/* 뒤로가기 버튼 */}
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackToList}
          sx={{ mb: 3 }}
        >
          목록으로 돌아가기
        </Button>

        {/* 공지사항 정보 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {isEditMode ? (
            // 수정 모드
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  제목
                </Typography>
                <TextField
                  fullWidth
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  disabled={updating}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  내용
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  disabled={updating}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editIsUrgent}
                      onChange={(e) => setEditIsUrgent(e.target.checked)}
                      disabled={updating}
                    />
                  }
                  label={
                    <Typography variant="body1" fontWeight={600}>
                      긴급 공지
                    </Typography>
                  }
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancelEdit}
                  disabled={updating}
                >
                  취소
                </Button>
                <Button
                  variant="contained"
                  startIcon={updating ? <CircularProgress size={20} /> : <Save />}
                  onClick={handleUpdate}
                  disabled={updating || !editTitle.trim() || !editContent.trim()}
                >
                  저장
                </Button>
              </Box>
            </>
          ) : (
            // 읽기 모드
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h5" fontWeight={700}>
                    {notice.title}
                  </Typography>
                  {notice.isUrgent && (
                    <Chip label="긴급" color="error" size="small" />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={handleEditMode}
                    size="small"
                  >
                    수정
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={handleDeleteClick}
                    size="small"
                  >
                    삭제
                  </Button>
                </Box>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
                작성일:{' '}
                {notice.createdAt
                  ? format(notice.createdAt.toDate(), 'yyyy년 MM월 dd일 HH:mm', {
                      locale: ko,
                    })
                  : '-'}
              </Typography>

              <Box
                sx={{
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.8,
                  }}
                >
                  {notice.content}
                </Typography>
              </Box>
            </>
          )}
        </Paper>

        {/* 삭제 확인 다이얼로그 */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
          <DialogTitle>공지사항 삭제</DialogTitle>
          <DialogContent>
            <DialogContentText>
              이 공지사항을 삭제하시겠습니까?
              <br />
              삭제된 공지사항은 복구할 수 없습니다.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={deleting}>
              취소
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} /> : undefined}
            >
              삭제
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};
